import React, {PropsWithChildren, useId, useState} from "react"
import {IcomoonIconName} from "../Icon/icons"
import Skeleton from "react-loading-skeleton"
import classNames from "classnames"
import Icon from "../Icon/Icon"

export type AccordionTheme = "light" | "full"

interface AccordionProps {
	loading?: boolean
	className?: string
	theme: AccordionTheme
	icon?: IcomoonIconName
	noContentPadding?: boolean
	initiallyExpanded?: boolean
	title: React.ReactNode | string
	uncontrolled?: {
		expanded: boolean
		onExpand: () => void
	}
	rightSideAction?: React.ReactNode
}

interface AccordionSharedProps {
	headerId: string
	contentId: string
	themeClass: string
	expandable: boolean
	icon?: IcomoonIconName
	noContentPadding?: boolean
	title: React.ReactNode | string
	rightSideAction?: React.ReactNode
}

interface AccordionHeaderProps extends AccordionSharedProps {
	expanded?: boolean
	handleClick: () => void
}

interface AccordionContentProps extends AccordionSharedProps {
	expanded?: boolean
}

export function Accordion({
	icon,
	title,
	theme,
	loading,
	children,
	className,
	uncontrolled,
	noContentPadding,
	initiallyExpanded,
	rightSideAction,
}: PropsWithChildren<AccordionProps>) {
	const id = useId()
	const headerId = `header-${id}`
	const contentId = `content-${id}`

	const expandable = Boolean(children)

	const themeMap: Record<AccordionTheme, string> = {
		light: "light-theme",
		full: "full-theme",
	}
	const themeClass = themeMap[theme]

	const commonProps = {icon, title, headerId, contentId, themeClass, expandable, noContentPadding, rightSideAction}

	return loading ? (
		<AccordionSkeleton themeClass={themeClass} />
	) : (
		<div className={classNames("accordion-container", themeClass, expandable && "expandable", className)}>
			{uncontrolled ? (
				<UncontrolledAccordion {...commonProps} uncontrolled={uncontrolled}>
					{children}
				</UncontrolledAccordion>
			) : (
				<ControlledAccordion {...commonProps} initiallyExpanded={initiallyExpanded}>
					{children}
				</ControlledAccordion>
			)}
		</div>
	)
}

function UncontrolledAccordion({
	uncontrolled,
	children,
	...props
}: PropsWithChildren<
	{
		uncontrolled: {expanded: boolean; onExpand: () => void}
	} & AccordionSharedProps
>) {
	const {expanded, onExpand} = uncontrolled

	return (
		<>
			<AccordionHeader {...props} expanded={expanded} handleClick={onExpand} />
			{props.expandable && (
				<AccordionContent {...props} expanded={expanded}>
					{children}
				</AccordionContent>
			)}
		</>
	)
}

function ControlledAccordion({
	initiallyExpanded,
	children,
	...props
}: PropsWithChildren<
	{
		initiallyExpanded?: boolean
	} & AccordionSharedProps
>) {
	const [expanded, setExpanded] = useState(props.expandable && initiallyExpanded)

	const handleToggle = () => setExpanded((prev) => !prev)

	return (
		<>
			<AccordionHeader {...props} expanded={expanded} handleClick={handleToggle} />
			{props.expandable && (
				<AccordionContent {...props} expanded={expanded}>
					{children}
				</AccordionContent>
			)}
		</>
	)
}

function AccordionContent(props: PropsWithChildren<AccordionContentProps>) {
	const {headerId, contentId, expanded, themeClass, noContentPadding, children} = props
	return (
		<div className={classNames("accordion accordion-content-wrapper", themeClass, expanded && "expanded")}>
			<div className="accordion-content" role="region" id={contentId} aria-labelledby={headerId}>
				<div className={classNames("accordion-content-inner-decoration", themeClass)}>
					<div className={classNames(!noContentPadding && "padded")}>{children}</div>
				</div>
			</div>
		</div>
	)
}

function AccordionHeader(props: AccordionHeaderProps) {
	const {themeClass, expandable, title, expanded, contentId, headerId, icon, handleClick, rightSideAction} = props

	return (
		<button
			className={classNames(
				"accordion-header",
				themeClass,
				"is-flex",
				"is-justify-content-space-between",
				expandable && "expandable",
				expanded && "expanded"
			)}
			onClick={() => expandable && handleClick()}
			aria-controls={contentId}
			aria-expanded={expanded}
			id={headerId}
		>
			<div className="icon-text">
				{icon && (
					<span className="icon">
						<Icon icon={icon} size={12} />
					</span>
				)}
				<span className={classNames("accordion-title", themeClass)}>{title}</span>
			</div>
			{rightSideAction}
			{expandable && !rightSideAction && (
				<span className="icon is-flex">
					<Icon
						icon="down-arrow-filter-chevron-small"
						className={classNames("accordion-icon", expanded && "expanded")}
						size={20}
					/>
				</span>
			)}
		</button>
	)
}

function AccordionSkeleton(props: {themeClass: string}) {
	const {themeClass} = props
	return (
		<div className={classNames("accordion-container", "skeleton-container", themeClass)}>
			<Skeleton />
		</div>
	)
}
